import React from 'react';
import { Box, Chip, Grid, Typography, Link } from '@mui/material';
import DataGrid from '../../common/ResponsiveDataGrid'
import { parseDateAndTime, addressStringWithPrefix, formatCurrency, shouldMaskData, maskPhoneNumber, maskEmail, maskAddress } from '../../helpers/UtilityHelper';
import { useSelector } from 'react-redux';
import jwt_decode from "jwt-decode"
import { getToken } from '../../helpers/ReduxHelpers';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { NAVY, BORDER, SUBTLE, WHITE, LIGHT_BG, PRIMARY_BLUE, SUCCESS, ERROR, withOpacity } from '../../styles/foundation/colors';

const columns = [
    {
        field: 'item_name',
        headerName: 'Product name',
        flex: 2
    },
    {
        field: 'sku_id',
        headerName: 'SKU',
        flex: 1
    },
    {
        field: 'units',
        headerName: 'Qty',
        flex: 0.8
    },
    {
        field: 'tax',
        headerName: 'Tax',
        flex: 1,
        // type: 'number',
        valueFormatter: (params) => {
            if (params.value == null) {
                return '';
            }

            const valueFormatted = Number(params.value).toLocaleString();
            return `${valueFormatted} %`;
        },
    },
    {
        field: 'selling_price',
        headerName: 'Price',
        flex: 1,
        // type: "number",
        valueFormatter: (params) => {
            if (params.value == null) {
                return '';
            }

            const valueFormatted = Number(params.value).toLocaleString();
            return `₹ ${valueFormatted}`;
        },
    }
]

const cardSx = { border: `1px solid ${BORDER}`, borderRadius: '12px', bgcolor: WHITE, overflow: 'hidden', mb: 2 };
const cardHeadSx = { display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, borderBottom: `1px solid ${BORDER}`, bgcolor: LIGHT_BG };
const cardIconSx = (color) => ({ width: 30, height: 30, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: withOpacity(color, 0.1), color, flexShrink: 0, '& svg': { fontSize: 17 } });

const SectionCard = ({ icon, title, color = PRIMARY_BLUE, children }) => (
    <Box sx={cardSx}>
        <Box sx={cardHeadSx}>
            <Box sx={cardIconSx(color)}>{icon}</Box>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY }}>{title}</Typography>
        </Box>
        <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
);

const Field = ({ label, value, color }) => (
    <Grid item xs={6} md={3}>
        <Typography sx={{ fontFamily: FONT, fontSize: '10px', fontWeight: WEIGHT.bold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
        <Box sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: color || NAVY, mt: 0.3 }}>{value ?? '—'}</Box>
    </Grid>
);

const PiiRow = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, py: 0.75, borderBottom: `1px solid ${BORDER}`, '&:last-of-type': { borderBottom: 'none' } }}>
        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, flexShrink: 0 }}>{label}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: NAVY, textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</Typography>
    </Box>
);

const OrderView = ({ resData }) => {
    const orderDetails = (resData && resData?.orderSearch?.data?.data[0]) || null;
    const itemDetails = ((orderDetails) ? orderDetails.item_list : [])
    const payment_mode = ((orderDetails && orderDetails.payment_mode) ? orderDetails.payment_mode : "");
    const color = payment_mode === 'COD' ? "error" : "success";
    const currentStatus = ((orderDetails && orderDetails.orderStatus) ? orderDetails.orderStatus : "");
    const { report } = useSelector(state => state);
    const { piiAccessAllow } = jwt_decode(getToken());

    function getStatusColor() {
        if (['FAILED_DELIVERY', "SHIPMENT_HELD", 'RTO', "RTO_INTRANSIT"].includes(currentStatus)) {
            return ERROR
        }
        if (['DELIVERED', "RTO_DELIVERED"].includes(currentStatus)) {
            return SUCCESS
        }
        return PRIMARY_BLUE
    }

    return (
        <>
            {/* Reference + status strip */}
            <Box sx={{
                display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', bgcolor: WHITE, border: `1px solid ${BORDER}`,
                borderRadius: '12px', overflow: 'hidden', mb: 2,
            }}>
                <Box sx={{ p: 2, textAlign: 'center', borderRight: `1px solid ${BORDER}` }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.lg, fontWeight: WEIGHT.extra, color: NAVY }}>{orderDetails?.shippingLabelId || '—'}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.3 }}>Order Reference Number</Typography>
                </Box>
                <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.lg, fontWeight: WEIGHT.extra, color: getStatusColor() }}>{currentStatus.replaceAll("_", " ") || '—'}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.3 }}>Current Status</Typography>
                </Box>
            </Box>

            <SectionCard icon={<LocalShippingOutlinedIcon />} title="Order Details">
                <Grid container spacing={2.5}>
                    <Field label="Order Number" value={orderDetails?.shippingLabelId} />
                    <Field label="Channel" value={orderDetails?.channel_name} />
                    <Field label="AWB Number" value={orderDetails?.awb_number} />
                    <Field label="Current Status" value={currentStatus && <Chip variant="filled" size="small" label={currentStatus} />} />
                    <Field label="Payment Mode" value={payment_mode && <Chip size="small" color={color} label={payment_mode} />} />
                    <Field label="Shipping Partner" value={orderDetails?.courierDetail?.parent?.includes('Blended') ? orderDetails.actualCourierProviderName : orderDetails?.courierDetail?.parent} />
                    <Field label="Invoice Value" value={orderDetails?.invoice_value ? formatCurrency(orderDetails.invoice_value) : null} />
                    <Field label="Order Date" value={orderDetails?.order_date ? parseDateAndTime(orderDetails.order_date, true) : null} />
                    <Field label="Invoice Number" value={orderDetails?.invoice_number} />
                    <Field label="Shipping Cost" value={orderDetails?.merchant_pricing?.price ? formatCurrency(orderDetails.merchant_pricing.price) : null} />
                    <Field label="Shipping Zone" value={orderDetails?.merchantZone} />
                    <Field label="EDD" value={orderDetails?.edd ? parseDateAndTime(orderDetails.edd, true) : null} />
                    <Field label="Delivery Date" value={orderDetails?.actualDeliveryTime ? parseDateAndTime(orderDetails.actualDeliveryTime, true) : null} />
                    <Field label="Dead Weight" value={orderDetails?.shipment_detail?.[0]?.item_weight ? `${(orderDetails.shipment_detail[0].item_weight / 1000).toFixed(2)} kg` : null} />
                    {orderDetails?.manifestDetails && (
                        <>
                            <Field label="Manifest Number" value={orderDetails.manifestDetails?.manifestNumber} />
                            <Field label="Manifest Link" value={<Link target='_blank' href={orderDetails?.manifestDetails?.manifestUrl}>Manifest</Link>} />
                        </>
                    )}
                </Grid>
            </SectionCard>

            {orderDetails?.isWDSetteled === true &&
                <SectionCard icon={<LocalShippingOutlinedIcon />} title="Weight Discrepancy" color={ERROR}>
                    <Grid container spacing={2.5}>
                        <Field label="Weight Discrepancy Status" value={orderDetails?.wdStatus} />
                        <Field label="Updated Merchant Price" value={orderDetails?.wdChargedPriceMerchant ? formatCurrency(orderDetails.wdChargedPriceMerchant) : null} />
                        <Field label="Updated Weight" value={orderDetails?.wdChargedWeight ? `${orderDetails.wdChargedWeight / 1000} kg` : null} />
                    </Grid>
                </SectionCard>
            }

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                    <SectionCard icon={<PersonOutlineOutlinedIcon />} title="Pickup Details">
                        <PiiRow label="Pickup Name" value={orderDetails?.pickup_details?.from_name} />
                        <PiiRow label="Phone Number" value={orderDetails?.pickup_details?.from_phone_number} />
                        <PiiRow label="Email ID" value={orderDetails?.pickup_details?.from_email} />
                        <PiiRow label="Address" value={addressStringWithPrefix("from", orderDetails?.pickup_details)} />
                    </SectionCard>
                </Grid>
                <Grid item xs={12} md={6}>
                    <SectionCard icon={<PlaceOutlinedIcon />} title="Delivery Details">
                        <PiiRow label="Customer Name" value={orderDetails?.delivery_details?.to_name} />
                        <PiiRow label="Phone Number" value={
                            orderDetails?.terminalStatus
                                ? 'xxxxxxxxxx'
                                : (shouldMaskData(report, orderDetails, piiAccessAllow)
                                    ? maskPhoneNumber(orderDetails?.delivery_details?.to_phone_number)
                                    : orderDetails?.delivery_details?.to_phone_number)
                        } />
                        <PiiRow label="Email ID" value={
                            shouldMaskData(report, orderDetails, piiAccessAllow)
                                ? maskEmail(orderDetails?.delivery_details?.to_email)
                                : orderDetails?.delivery_details?.to_email
                        } />
                        <PiiRow label="Shipping Address" value={
                            shouldMaskData(report, orderDetails, piiAccessAllow)
                                ? maskAddress(addressStringWithPrefix("to", orderDetails?.delivery_details))
                                : addressStringWithPrefix("to", orderDetails?.delivery_details)
                        } />
                    </SectionCard>
                </Grid>
            </Grid>

            {itemDetails && itemDetails.length > 0 && (
                <Box sx={{
                    ...cardSx,
                    '& .MuiDataGrid-root': { border: 'none' },
                    '& .MuiDataGrid-columnHeaders': { backgroundColor: LIGHT_BG, borderBottom: `1px solid ${BORDER}` },
                    '& .MuiDataGrid-columnHeaderTitle': { fontFamily: FONT, fontWeight: WEIGHT.extra, fontSize: '0.7rem', color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.03em' },
                    '& .MuiDataGrid-cell': { borderBottom: `1px solid ${BORDER}`, fontFamily: FONT, fontSize: '0.8rem' },
                }}>
                    <DataGrid
                        columns={columns}
                        getRowId={(row) => Math.random()}
                        rows={itemDetails || []}
                        hideFooterPagination={true}
                        autoHeight
                        disableColumnMenu
                        disableColumnFilter
                        disableSelectionOnClick
                    />
                </Box>
            )}
        </>
    );
}

export default OrderView
